// Lottery days on the member tee sheet (F1–F2). Until a lottery date is revealed its tee times
// can't be booked; instead, while requests are open, a member asks for a time range with their
// playing partners. One request per member per date; the organizer changes or withdraws it,
// partners can leave it, until requests close.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  LotteryRequest,
  LotteryRequestChange,
  LotteryRequestInput,
  LotteryStatus,
  PlayingPartner,
} from "@union/contracts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@union/ui/components/alert-dialog";
import { Badge } from "@union/ui/components/badge";
import { Button } from "@union/ui/components/button";
import { Card } from "@union/ui/components/card";
import { Checkbox } from "@union/ui/components/checkbox";
import { Field, FieldError, FieldLabel } from "@union/ui/components/field";
import { Input } from "@union/ui/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@union/ui/components/select";
import { ApiRequestError, apiGet, apiSend } from "@union/web-kit";
import { Ticket, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useClubSession } from "./club-session.ts";

/** A group is the organizer plus up to three partners (a tee time's four). */
const MAX_PARTNERS = 3;

const problem = (error: unknown, fallback: string) =>
  error instanceof ApiRequestError && error.detail ? error.detail : fallback;

function fail(): never {
  throw new Error("session required");
}

/** "Sat 24 Oct at 08:00", in the club's timezone. */
function when(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: timezone,
  })
    .format(new Date(instant))
    .replace(",", "")
    .replace(/ (\d\d:\d\d)$/, " at $1");
}

export function LotteryPanel({
  slug,
  date,
  status,
  courseName,
  times,
  timezone,
}: {
  slug: string;
  date: string;
  status: LotteryStatus;
  courseName: string;
  /** The course's tee times that day (HH:MM), for the range. */
  times: string[];
  timezone: string;
}) {
  const [editing, setEditing] = useState(false);
  const now = Date.now();
  // Revealed when staff (or the hourly job) reveal it, not at the clock time (F4).
  const stage = status.revealedAt
    ? "revealed"
    : now < Date.parse(status.requestsOpenAt)
      ? "before"
      : now < Date.parse(status.requestsCloseAt)
        ? "open"
        : "drawing";
  const mine = status.requests[0] ?? null;
  // After the reveal: a placed group is booked ("You're on…" shows it); say so if it wasn't placed.
  if (stage === "revealed") {
    if (!mine?.draw || mine.draw.teeTimeId) return null;
    return (
      <Card className="flex-row items-start gap-space-3 px-space-5 py-space-4" data-testid="lottery">
        <Ticket aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
        <div className="grid gap-space-1">
          <p className="type-title">Your group wasn’t placed on {courseName}</p>
          <p className="type-body text-muted-foreground">
            The lottery couldn’t fit your request. Book any open time below.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="gap-space-3 px-space-5 py-space-4" data-testid="lottery">
      <div className="flex items-start gap-space-3">
        <Ticket aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-primary-text" />
        <div className="grid gap-space-1">
          <p className="type-title">Lottery day on {courseName}</p>
          <p className="type-body text-muted-foreground">
            {stage === "before" &&
              `Requests open ${when(status.requestsOpenAt, timezone)} and close ${when(status.requestsCloseAt, timezone)}.`}
            {stage === "open" &&
              `Ask for a time range with your group by ${when(status.requestsCloseAt, timezone)}. Times are drawn and revealed ${when(status.revealsAt, timezone)}.`}
            {stage === "drawing" &&
              (now < Date.parse(status.revealsAt)
                ? `Requests have closed. Times are revealed ${when(status.revealsAt, timezone)}.`
                : "Requests have closed. Times are being revealed.")}
          </p>
        </div>
      </div>

      {mine && !editing && (
        <RequestSummary
          slug={slug}
          request={mine}
          canChange={stage === "open"}
          onChange={() => setEditing(true)}
        />
      )}
      {stage === "open" && (editing || !mine) && (
        <RequestForm
          slug={slug}
          date={date}
          courseId={status.courseId}
          times={times}
          request={mine}
          onDone={() => setEditing(false)}
        />
      )}
    </Card>
  );
}

/** The member's request: the range, the group, and (while open) change or leave. */
function RequestSummary({
  slug,
  request,
  canChange,
  onChange,
}: {
  slug: string;
  request: LotteryRequest;
  canChange: boolean;
  onChange: () => void;
}) {
  const session = useClubSession();
  const queryClient = useQueryClient();
  const organizer = request.players.find((p) => p.isOrganizer);
  const iOrganize = organizer?.isMe ?? false;
  const leave = useMutation({
    mutationFn: () =>
      apiSend<{ outcome: "withdrawn" | "left" }>(
        "DELETE",
        `/member/clubs/${slug}/lottery-requests/${request.id}`,
        session ?? fail(),
      ),
    onSuccess: ({ outcome }) =>
      toast.success(outcome === "withdrawn" ? "Request withdrawn." : "You’ve left the request."),
    onError: (error) => toast.error(problem(error, "Couldn’t do that. Try again.")),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] }),
  });
  return (
    <div className="grid gap-space-2 rounded-md border border-border bg-background px-space-4 py-space-3">
      <div className="flex flex-wrap items-center gap-space-2">
        <Badge variant="primary">Requested</Badge>
        <span className="type-time">
          {request.earliest}–{request.latest}
        </span>
        {request.laterOk && <span className="type-caption text-muted-foreground">later is fine</span>}
      </div>
      <p className="type-body">
        {request.players.map((p) => (p.isMe ? "You" : p.name)).join(", ")}
        {!iOrganize && organizer && (
          <span className="type-caption text-muted-foreground"> · requested by {organizer.name}</span>
        )}
      </p>
      {canChange && (
        <div className="flex flex-wrap gap-space-2">
          {iOrganize && (
            <Button size="sm" variant="outline" onClick={onChange}>
              Change
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-destructive" disabled={leave.isPending}>
                {iOrganize ? "Withdraw" : "Leave this request"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {iOrganize ? "Withdraw your request?" : "Leave this request?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {iOrganize
                    ? "Your whole group comes out of the lottery for this day. You can ask again while requests are open."
                    : "The rest of the group stays in. You can make your own request while requests are open."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction variant="destructive" onClick={() => leave.mutate()}>
                  {iOrganize ? "Withdraw" : "Leave"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

/** Make or change a request: the range, partners, and whether later is fine. */
function RequestForm({
  slug,
  date,
  courseId,
  times,
  request,
  onDone,
}: {
  slug: string;
  date: string;
  courseId: string;
  times: string[];
  request: LotteryRequest | null;
  onDone: () => void;
}) {
  const session = useClubSession();
  const queryClient = useQueryClient();
  const choices = [...new Set(times)].sort();
  const [earliest, setEarliest] = useState(request?.earliest ?? choices[0] ?? "07:00");
  const [latest, setLatest] = useState(
    request?.latest ?? choices[Math.min(6, choices.length - 1)] ?? "09:00",
  );
  const [laterOk, setLaterOk] = useState(request?.laterOk ?? true);
  const [partners, setPartners] = useState<PlayingPartner[]>(
    request?.players
      .filter((p) => !p.isMe)
      .map((p) => ({ membershipId: p.membershipId, name: p.name, memberNumber: p.memberNumber })) ?? [],
  );
  const [query, setQuery] = useState("");
  const search = useQuery({
    queryKey: ["playing-partners", slug, query.trim().toLowerCase()],
    queryFn: () =>
      apiGet<{ partners: PlayingPartner[] }>(
        `/member/clubs/${slug}/playing-partners?q=${encodeURIComponent(query.trim())}`,
        session ?? fail(),
      ),
    enabled: !!session && query.trim().length >= 2,
  });
  const found = (search.data?.partners ?? []).filter(
    (p) => !partners.some((x) => x.membershipId === p.membershipId),
  );

  const save = useMutation({
    mutationFn: () => {
      const change: LotteryRequestChange = {
        earliest,
        latest,
        laterOk,
        partnerIds: partners.map((p) => p.membershipId),
      };
      if (request) {
        return apiSend<LotteryRequest>(
          "PUT",
          `/member/clubs/${slug}/lottery-requests/${request.id}`,
          session ?? fail(),
          { body: change },
        );
      }
      const body: LotteryRequestInput = { courseId, date, ...change };
      return apiSend<LotteryRequest>("POST", `/member/clubs/${slug}/lottery-requests`, session ?? fail(), {
        body,
      });
    },
    onSuccess: () => {
      toast.success(request ? "Request updated." : "Request sent. Your group can see it.");
      onDone();
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] }),
  });

  const timeSelect = (id: string, label: string, value: string, set: (v: string) => void) => (
    <Field className="w-auto">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select value={value} onValueChange={set}>
        <SelectTrigger id={id} className="w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {[...new Set([...choices, value])].sort().map((t) => (
            <SelectItem key={t} value={t}>
              {t}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );

  return (
    <form
      className="grid gap-space-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex flex-wrap items-end gap-space-3">
        {timeSelect("lottery-earliest", "Between", earliest, setEarliest)}
        {timeSelect("lottery-latest", "and", latest, setLatest)}
      </div>
      <Field orientation="horizontal">
        <Checkbox id="lottery-later" checked={laterOk} onCheckedChange={(v) => setLaterOk(v === true)} />
        <FieldLabel htmlFor="lottery-later">A later time is fine if those are full</FieldLabel>
      </Field>

      <Field>
        <FieldLabel htmlFor="lottery-partner">Playing partners</FieldLabel>
        {partners.length > 0 && (
          <ul className="flex flex-wrap gap-space-2" aria-label="Your playing partners">
            {partners.map((p) => (
              <li key={p.membershipId}>
                <Badge variant="secondary" className="gap-space-1 pr-space-1">
                  {p.name}
                  {p.memberNumber && <span className="type-time opacity-70">{p.memberNumber}</span>}
                  <button
                    type="button"
                    className="grid size-6 place-items-center rounded-full hover:bg-accent"
                    aria-label={`Remove ${p.name}`}
                    onClick={() => setPartners(partners.filter((x) => x.membershipId !== p.membershipId))}
                  >
                    <X aria-hidden="true" className="size-3.5" />
                  </button>
                </Badge>
              </li>
            ))}
          </ul>
        )}
        {partners.length < MAX_PARTNERS ? (
          <>
            <Input
              id="lottery-partner"
              placeholder="Search members by name"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query.trim().length >= 2 && (
              <ul className="grid gap-space-1" aria-label="Members found">
                {found.map((p) => (
                  <li key={p.membershipId}>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => {
                        setPartners([...partners, p]);
                        setQuery("");
                      }}
                    >
                      Add {p.name}
                      {p.memberNumber && (
                        <span className="type-time text-muted-foreground">{p.memberNumber}</span>
                      )}
                    </Button>
                  </li>
                ))}
                {search.data && found.length === 0 && (
                  <li className="type-caption text-muted-foreground">No members found.</li>
                )}
              </ul>
            )}
          </>
        ) : (
          <p className="type-caption text-muted-foreground">That’s a full group.</p>
        )}
      </Field>

      {save.isError && (
        <FieldError>{problem(save.error, "Couldn’t send your request. Try again.")}</FieldError>
      )}
      <div className="flex flex-wrap gap-space-2">
        <Button type="submit" disabled={save.isPending || latest < earliest}>
          {save.isPending ? "Sending…" : request ? "Save request" : "Request a time"}
        </Button>
        {request && (
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
